import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { PurchaseRequestService } from '../purchase-request.service';
import {
  PurchaseRequestRecord,
  PurchaseRequestStatus,
  PurchaseRequestPurpose,
  PURCHASE_REQUEST_STATUS_SEVERITY,
  PURCHASE_REQUEST_PURPOSE_SEVERITY,
} from '../purchase-request.config';
import { PurchaseRequestItem } from '../models/purchase-request-item';
import {
  AddFormComponent,
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';

@Component({
  selector: 'app-purchase-request-view',
  standalone: true,
  imports: [CommonModule, TranslateModule, ToastModule, AddFormComponent],
  providers: [MessageService],
  templateUrl: './purchase-request-view.component.html',
  styleUrl: './purchase-request-view.component.css',
})
export class PurchaseRequestViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Data
  purchaseRequestData = signal<PurchaseRequestRecord | null>(null);
  itemsList = signal<PurchaseRequestItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;

  // Form configuration
  formFields = signal<FormField[]>([]);
  tableSections = signal<TableSection[]>([]);
  headerActions = signal<ActionButton[]>([]);

  // Constants
  readonly PurchaseRequestStatus = PurchaseRequestStatus;
  readonly PurchaseRequestPurpose = PurchaseRequestPurpose;

  ngOnInit(): void {
    this.initializeFormFields();
    this.initializeTableSections();
    this.initializeHeaderActions();

    const id = parsePositiveIntRouteParam(this.route.snapshot.paramMap.get('id'));
    if (id != null) {
      this.loadPurchaseRequestData(id);
    } else {
      this.router.navigate(['/procurement/purchase-request']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPurchaseRequestData(id: number): void {
    this.loading.set(true);

    this.purchaseRequestService
      .getPurchaseRequestById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.purchaseRequestData.set(response);
            this.populateFormData(response);
            // Load items from API response
            this.loadItemsFromApiResponse(response);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'purchase_request.messages.not_found'
              ),
            });
            this.router.navigate(['/procurement/purchase-request']);
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_load_data'
            ),
          });
          this.router.navigate(['/procurement/purchase-request']);
        },
      });
  }

  onBack(): void {
    this.router.navigate(['/procurement/purchase-request']);
  }

  private populateFormData(data: PurchaseRequestRecord): void {
    const scheduleDate = data.scheduleDate
      ? new Date(data.scheduleDate)
      : new Date();

    // Set the current form data to be used by the add-form component
    this.currentFormData = {
      series: data.series || '',
      title: data.title,
      scheduleDate: scheduleDate,
      status: data.status || 'PENDING_APPROVAL',
    };

    // Update header actions after data is loaded
    this.initializeHeaderActions();
  }

  private loadItemsFromApiResponse(data: PurchaseRequestRecord): void {
    // Use service method to map API items to PurchaseRequestItem format
    const mappedItems =
      this.purchaseRequestService.mapApiItemsToPurchaseRequestItems(
        data.items || []
      );

    this.itemsList.set(mappedItems);
    this.updateItemsTableData();
  }

  getStatusSeverity(
    status: string
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return PURCHASE_REQUEST_STATUS_SEVERITY[status] || 'secondary';
  }

  getPurposeSeverity(
    purpose: string
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return PURCHASE_REQUEST_PURPOSE_SEVERITY[purpose] || 'secondary';
  }

  getStatusLabel(status: string): string {
    return this.translateService.instant(
      `purchase_request.status.${status.toLowerCase()}`
    );
  }

  getPurposeLabel(purpose: string): string {
    return this.translateService.instant(
      `purchase_request.purpose.${purpose.toLowerCase()}`
    );
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  private initializeFormFields(): void {
    this.formFields.set([
      {
        label: this.translateService.instant('purchase_request.fields.series'),
        controlName: 'series',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_series'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 50,
        disabled: true,
      },
      {
        label: this.translateService.instant('purchase_request.fields.title'),
        controlName: 'title',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_title'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 200,
        disabled: true,
      },
      {
        label: this.translateService.instant(
          'purchase_request.fields.schedule_date'
        ),
        controlName: 'scheduleDate',
        type: 'date',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.select_schedule_date'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        disabled: true,
        minDate: new Date('2025-1-1'),
      },
    ]);
  }

  private initializeTableSections(): void {
    this.tableSections.set([
      {
        testIdKey: 'purchase_request.sections.item_details',
        title: this.translateService.instant(
          'purchase_request.sections.item_details'
        ),
        show: true,
        columns: [
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_code'
            ),
            prop: 'spuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_name'
            ),
            prop: 'spuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_code'
            ),
            prop: 'skuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_name'
            ),
            prop: 'skuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.quantity'
            ),
            prop: 'quantity',
            width: 120,
            type: 'number',
            useGrouping: true,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.warehouse_code'
            ),
            prop: 'warehouseCode',
            width: 150,
            type: 'text',
          },
        ],
        data: [],
        hasCheckbox: false,
        actions: [],
      },
    ]);
  }

  private initializeHeaderActions(): void {
    const actions = [
      {
        label: this.translateService.instant('purchase_request.actions.back'),
        action: 'back',
        severity: 'secondary' as const,
        outlined: true,
      },
    ];

    // Add create purchase order button if status is approved and no orders exist
    if (this.isStatusApproved() && !this.checkPurchaseOrderExists()) {
      actions.splice(1, 0, {
        label: this.translateService.instant(
          'purchase_request.actions.create_purchase_order'
        ),
        action: 'createPurchaseOrder',
        severity: 'secondary' as const,
        outlined: false,
      });
    }

    this.headerActions.set(actions);
  }

  private isStatusApproved(): boolean {
    return this.currentFormData?.status === PurchaseRequestStatus.ORDERED;
  }

  private checkPurchaseOrderExists(): boolean {
    const purchaseRequestData = this.purchaseRequestData();
    return !!purchaseRequestData?.orders?.length;
  }

  private getPurposeOptions(): any[] {
    return [
      {
        label: this.translateService.instant(
          'purchase_request.purpose.purchase'
        ),
        value: PurchaseRequestPurpose.PURCHASE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_transfer'
        ),
        value: PurchaseRequestPurpose.MATERIAL_TRANSFER,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_issue'
        ),
        value: PurchaseRequestPurpose.MATERIAL_ISSUE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.manufacture'
        ),
        value: PurchaseRequestPurpose.MANUFACTURE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.customer_provided'
        ),
        value: PurchaseRequestPurpose.CUSTOMER_PROVIDED,
      },
    ];
  }

  private getStatusOptions(): any[] {
    return [
      {
        label: this.translateService.instant('purchase_request.status.draft'),
        value: PurchaseRequestStatus.DRAFT,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.submitted'
        ),
        value: PurchaseRequestStatus.SUBMITTED,
      },
      {
        label: this.translateService.instant('purchase_request.status.stopped'),
        value: PurchaseRequestStatus.STOPPED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.cancelled'
        ),
        value: PurchaseRequestStatus.CANCELLED,
      },
      {
        label: this.translateService.instant('purchase_request.status.pending'),
        value: PurchaseRequestStatus.PENDING,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.partially_ordered'
        ),
        value: PurchaseRequestStatus.PARTIALLY_ORDERED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.partially_received'
        ),
        value: PurchaseRequestStatus.PARTIALLY_RECEIVED,
      },
      {
        label: this.translateService.instant('purchase_request.status.ordered'),
        value: PurchaseRequestStatus.ORDERED,
      },
      {
        label: this.translateService.instant('purchase_request.status.issued'),
        value: PurchaseRequestStatus.ISSUED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.transferred'
        ),
        value: PurchaseRequestStatus.TRANSFERRED,
      },
      {
        label: this.translateService.instant(
          'purchase_request.status.received'
        ),
        value: PurchaseRequestStatus.RECEIVED,
      },
    ];
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'back') {
      this.router.navigate(['/procurement/purchase-request']);
    } else if (event.action === 'createPurchaseOrder') {
      this.onCreatePurchaseOrder();
    }
  }

  private onCreatePurchaseOrder(): void {
    const purchaseRequestData = this.purchaseRequestData();
    if (purchaseRequestData?.id) {
      this.router.navigate(['/procurement/purchase-order/add'], {
        queryParams: { purchaseRequestId: purchaseRequestData.id },
      });
    }
  }

  onTableAction(event: { action: string; data?: any; section?: string }): void {
    // No table actions in view mode
  }

  onFormValueChanges(formData: any): void {
    // No form value changes in view mode
  }

  private updateItemsTableData(): void {
    const currentSections = this.tableSections();
    const updatedSections = currentSections.map((section) => {
      // Always set the data for the section
      const itemsWithStt = this.itemsList().map((item, index) => ({
        ...item,
        stt: index + 1,
      }));

      return {
        ...section,
        data: itemsWithStt,
      };
    });

    this.tableSections.set(updatedSections);
  }
}
